import { FC, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Spinner } from '@/components/Button';
import Text from '@/components/Text';

const SpinnerHolder = styled.div`
  text-align: center;
  margin-top: 20px;
`;

const LoaderText = styled(Text)`
  display: inline-block;
`;

const StyledSpinner = styled(Spinner)`
  width: 18px;
  height: 18px;
  margin-bottom: -5px;
`;

const Span = styled.span`
  width: 1px;
  height: 1px;
`;

interface Props {
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  component?: React.ReactNode;
}

const InfiniteLoader: FC<Props> = ({
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  component,
}) => {
  const spanRef = useRef(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;

      if (target.isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    if (!spanRef.current) return;

    const element = spanRef.current;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 1,
    });

    observer.observe(element);

    return () => observer.unobserve(element);
  }, [handleObserver, isFetchingNextPage]);

  if (!hasNextPage) return null;

  if (isFetchingNextPage)
    return component ? (
      <>{component}</>
    ) : (
      <SpinnerHolder>
        <StyledSpinner />
        <LoaderText $type="smallMedium">Loading data...</LoaderText>
      </SpinnerHolder>
    );

  return <Span ref={spanRef} />;
};

export default InfiniteLoader;
